import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useForm, useFieldArray, useWatch, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import BackofficeLayout from '@/components/layouts/BackofficeLayout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { DocumentFeeSelector } from '@/components/pricing/DocumentFeeSelector';
import { pricingTableFormSchema, PricingTableFormValues } from '@/schemas/pricingTableSchema';
import { documentTypeApi, pricingTableApi, feesApportionPartyApi } from '@/services/api';
import usePricingTableForm from '@/hooks/usePricingTableForm';
import { PricingTableTransactionFees } from '@/components/pricing/PricingTableTransactionFees';
import { FeeArraysProvider } from '@/components/pricing/DocumentFeeFieldArrays';
import { PricingTableDocumentFees } from '@/components/pricing/PricingTableDocumentFees';
import PricingTableDeliveryFees from '@/components/pricing/PricingTableDeliveryFees';
import { PricingTableCustomFormFees } from '@/components/pricing/PricingTableCustomFormFees';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';

const PricingTableNew = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { selectedRole } = useAuth();

  // Move all hooks to the top before any conditional returns
  const {
    form,
    onSubmit,
    isSubmitting
  } = usePricingTableForm();

  const { fields: documentFeeFields, append: appendDocumentFee, remove: removeDocumentFee } = useFieldArray({
    control: form.control,
    name: "document_fees"
  });

  const { fields: transactionFeeFields } = useFieldArray({
    control: form.control,
    name: "transaction_fees"
  });

  const { data: feeApportionParties = [] } = useQuery({
    queryKey: ['fee-apportion-parties'],
    queryFn: () => feesApportionPartyApi.list()
  });

  const { data: documentTypes = [], isLoading: loadingDocumentTypes } = useQuery({
    queryKey: ['document-types'],
    queryFn: () => documentTypeApi.list()
  });

  const [activeTab, setActiveTab] = useState('fees');
  
  const getDocumentTypeName = (id: number) => {
    const docType = documentTypes?.find(dt => dt.id === id);
    return docType ? docType.name : `Document Type ${id}`;
  };
  
  const handleBackClick = () => {
    navigate('/backoffice/pricing');
  };
  
  // Redirect platform users away from this page
  useEffect(() => {
    if (selectedRole?.type === 'platform') {
      toast({
        title: "Access Denied",
        description: "Platform users cannot create pricing tables.",
        variant: "destructive"
      });
      navigate('/backoffice/pricing');
    }
  }, [selectedRole, navigate, toast]);

  // Only allow management company users
  if (selectedRole?.type !== 'management_company') {
    return (
      <BackofficeLayout>
        <div className="container mx-auto py-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
            <p className="text-muted-foreground mb-4">
              Only management company users can create pricing tables.
            </p>
            <Button onClick={() => navigate('/backoffice/pricing')}>
              Back to Pricing Tables
            </Button>
          </div>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="icon" onClick={handleBackClick}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">Create Pricing Table</h1>
          </div>
          <Button 
            type="submit" 
            form="pricing-table-form"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create'}
          </Button>
        </div>
        
        <FormProvider {...form}>
          <Form {...form}>
            <form id="pricing-table-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>Enter the pricing table details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Pricing Table Name" {...field} />
                        </FormControl>
                        <FormDescription>
                          The name of the pricing table.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Enter a description"
                            className="resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Optional description for this pricing table.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
              
              <Tabs defaultValue="fees" value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="fees">Transaction Fees</TabsTrigger>
                  <TabsTrigger value="documents">Document Fees</TabsTrigger>
                  <TabsTrigger value="delivery">Delivery Fees</TabsTrigger>
                  <TabsTrigger value="custom-forms">Custom Form Fees</TabsTrigger>
                </TabsList>
                
                <TabsContent value="fees" className="space-y-4 pt-4">
                  <PricingTableTransactionFees 
                    feesApportionParties={feeApportionParties}
                  />
                </TabsContent>
                
                <TabsContent value="documents" className="space-y-4 pt-4">
                  <PricingTableDocumentFees
                    feesApportionParties={feeApportionParties}
                    documentTypes={documentTypes || []}
                  />
                </TabsContent>

                <TabsContent value="delivery" className="space-y-4 pt-4">
                  <PricingTableDeliveryFees
                    documentTypes={documentTypes || []}
                    feesApportionParties={feeApportionParties}
                  />
                </TabsContent>

                <TabsContent value="custom-forms" className="space-y-4 pt-4">
                  <PricingTableCustomFormFees
                    documentTypes={documentTypes || []}
                    feesApportionParties={feeApportionParties}
                  />
                </TabsContent>
              </Tabs>
            </form>
          </Form>
        </FormProvider>
      </div>
    </BackofficeLayout>
  );
};

export default PricingTableNew;
